/**
 * TALEPLER (Demands) endpoints — BusDemand / VisaDemand / GroupTicketDemand — migration
 * guide Bölüm 3.3 & 3.9. All routes confirmed against the real API — see api/types/demands.ts
 * header for the curl/swagger evidence and the EditGroupTicketDemand `airlinePreference`
 * server quirk.
 */
import { apiPost, apiPostSession } from '../client';
import type {
  BusDemand,
  BusDemandListResponse,
  GroupTicketDemand,
  GroupTicketDemandListResponse,
  VisaDemand,
  VisaDemandListResponse,
} from '../types/demands';

/* -------------------------------- Bus Demand -------------------------------- */

export function getBusDemands(): Promise<BusDemandListResponse> {
  return apiPostSession<BusDemandListResponse>('BusDemand/GetBusDemands');
}

export function getBusDemandById(id: string): Promise<BusDemandListResponse> {
  return apiPost<{ busDemand: Partial<BusDemand> }, BusDemandListResponse>('BusDemand/GetBusDemandById', {
    busDemand: { id },
  });
}

export function addBusDemand(busDemand: Omit<BusDemand, 'id'>): Promise<BusDemandListResponse> {
  return apiPost<{ busDemand: Omit<BusDemand, 'id'> }, BusDemandListResponse>('BusDemand/AddBusDemand', {
    busDemand,
  });
}

export function editBusDemand(busDemand: BusDemand & { id: string }): Promise<BusDemandListResponse> {
  return apiPost<{ busDemand: BusDemand & { id: string } }, BusDemandListResponse>('BusDemand/EditBusDemand', {
    busDemand,
  });
}

/* ------------------------------- Visa Demand ------------------------------- */

export function getVisaDemands(): Promise<VisaDemandListResponse> {
  return apiPostSession<VisaDemandListResponse>('VisaDemand/GetVisaDemands');
}

export function getVisaDemandById(id: string): Promise<VisaDemandListResponse> {
  return apiPost<{ visaDemand: Partial<VisaDemand> }, VisaDemandListResponse>('VisaDemand/GetVisaDemandById', {
    visaDemand: { id },
  });
}

export function addVisaDemand(visaDemand: Omit<VisaDemand, 'id'>): Promise<VisaDemandListResponse> {
  return apiPost<{ visaDemand: Omit<VisaDemand, 'id'> }, VisaDemandListResponse>('VisaDemand/AddVisaDemand', {
    visaDemand,
  });
}

export function editVisaDemand(visaDemand: VisaDemand & { id: string }): Promise<VisaDemandListResponse> {
  return apiPost<{ visaDemand: VisaDemand & { id: string } }, VisaDemandListResponse>(
    'VisaDemand/EditVisaDemand',
    { visaDemand }
  );
}

/* --------------------------- Group Ticket Demand --------------------------- */

export function getGroupTicketDemands(): Promise<GroupTicketDemandListResponse> {
  return apiPostSession<GroupTicketDemandListResponse>('GroupTicketDemand/GetGroupTicketDemands');
}

export function getGroupTicketDemandById(id: string): Promise<GroupTicketDemandListResponse> {
  return apiPost<{ groupTicketDemand: Partial<GroupTicketDemand> }, GroupTicketDemandListResponse>(
    'GroupTicketDemand/GetGroupTicketDemandById',
    { groupTicketDemand: { id } }
  );
}

export function addGroupTicketDemand(
  groupTicketDemand: Omit<GroupTicketDemand, 'id'>
): Promise<GroupTicketDemandListResponse> {
  return apiPost<{ groupTicketDemand: Omit<GroupTicketDemand, 'id'> }, GroupTicketDemandListResponse>(
    'GroupTicketDemand/AddGroupTicketDemand',
    { groupTicketDemand }
  );
}

/** NOTE: always pass `airlinePreference` (empty array is fine) — the real API 500s with a
 *  NullReferenceException on EditGroupTicketDemand when it's omitted/null. See
 *  api/types/demands.ts header for the confirmed curl evidence. */
export function editGroupTicketDemand(
  groupTicketDemand: GroupTicketDemand & { id: string; airlinePreference: string[] }
): Promise<GroupTicketDemandListResponse> {
  return apiPost<
    { groupTicketDemand: GroupTicketDemand & { id: string; airlinePreference: string[] } },
    GroupTicketDemandListResponse
  >('GroupTicketDemand/EditGroupTicketDemand', { groupTicketDemand });
}
